import { useEffect, useRef, useState } from "react";

import { Modal } from "../../ui/modal";
import settings from "../settings.module.css";
import { drawGame, readPalette } from "./hoop-draw";
import {
  COURT_HEIGHT,
  COURT_WIDTH,
  isOnBall,
  newGame,
  release,
  setDrag,
  step,
  type Point,
} from "./hoop-game";
import styles from "./logo-hoop.module.css";

/**
 * The logo upload's one small game (STORY-179).
 *
 * **Choosing a file no longer uploads it.** It opens this dialog, and the upload
 * runs when the dialog says it is finished. There are three ways to finish and
 * every one of them uploads: score a basket, miss and watch a stick figure dunk
 * it for you, or press Skip. Cancel is the only way out that uploads nothing.
 *
 * It sits on the logo only because that is the least-used upload in the product
 * — set once per clinic, by an admin — so it cannot slow anyone's day. It is
 * not a pattern for the radiograph or invoice uploads, which are daily work.
 *
 * ── Why `onFinish` is asked of the parent, not done here ───────────────────
 *
 * The upload itself (presign, `PUT`, confirm) already lives in the clinic
 * section with its own error handling and toast. This dialog only decides
 * *when*; it shows "Uploading…" until the parent's promise settles, and the
 * parent closes it. A refused upload therefore reads on the page the way it
 * always did.
 */
export function LogoHoopDialog({
  file,
  onFinish,
  onCancel,
}: {
  file: File;
  /** Run the real upload. The parent closes this dialog when it settles. */
  onFinish: () => Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [uploading, setUploading] = useState(false);

  // The latest callbacks, read from inside the animation frame without
  // restarting the game when the parent re-renders.
  const finish = useRef(onFinish);
  useEffect(() => {
    finish.current = onFinish;
  });
  const started = useRef(false);

  function begin(): void {
    if (started.current) return;
    started.current = true;
    setUploading(true);
    void finish.current();
  }

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d") ?? null;
    if (el === null || ctx === null) return;

    const dpr = window.devicePixelRatio || 1;
    el.width = COURT_WIDTH * dpr;
    el.height = COURT_HEIGHT * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const game = newGame(window.matchMedia("(prefers-reduced-motion: reduce)").matches);

    // The logo the player is about to upload, drawn on the tile they throw.
    const logo = new Image();
    const url = URL.createObjectURL(file);
    logo.src = url;

    function toCourt(event: PointerEvent | React.PointerEvent): Point {
      const box = (el as HTMLCanvasElement).getBoundingClientRect();
      return {
        x: ((event.clientX - box.left) * COURT_WIDTH) / box.width,
        y: ((event.clientY - box.top) * COURT_HEIGHT) / box.height,
      };
    }

    // **On the window, not the canvas.** A drag that leaves the box has to keep
    // working — the player pulls well past the edge — and a pointer that leaves
    // the frame altogether has to count as letting go, or the file stays held.
    const onMove = (event: PointerEvent): void => {
      if (game.drag !== null) setDrag(game, toCourt(event));
    };
    const onUp = (): void => release(game);
    const onCancelDrag = (): void => setDrag(game, null);
    const onLeave = (): void => release(game);
    const onDown = (event: PointerEvent): void => {
      const at = toCourt(event);
      if (isOnBall(game, at)) setDrag(game, at);
    };

    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancelDrag);
    window.addEventListener("blur", onLeave);
    document.documentElement.addEventListener("mouseleave", onLeave);

    let frame = 0;
    const tick = (): void => {
      step(game);
      drawGame(ctx, readPalette(el), game, logo);
      if (game.finished) {
        begin();
        return; // the last frame stays up behind "Uploading…"
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancelDrag);
      window.removeEventListener("blur", onLeave);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      URL.revokeObjectURL(url);
    };
    // `file` is the only input: `begin` reads refs, so the game is not restarted
    // when the parent re-renders.
  }, [file]);

  // Closing mid-upload would leave the page waiting on a dialog that is gone.
  const close = (): void => {
    if (!uploading) onCancel();
  };

  return (
    <Modal
      label="Upload your logo"
      onClose={close}
      testId="logo-hoop"
      panelClassName={styles.panel}
    >
      <div>
        <h3 className={styles.title}>Upload your logo</h3>
        <p className={styles.sub}>To finish uploading, score a basket.</p>
      </div>

      <div className={styles.frame}>
        <canvas
          ref={canvas}
          className={styles.canvas}
          role="img"
          aria-label="A basketball hoop. Pull the file straight down and let go to shoot it in."
          data-testid="logo-hoop-canvas"
        />
        {uploading && (
          <div className={styles.uploading} role="status" data-testid="logo-hoop-uploading">
            <span>Uploading&hellip;</span>
            <span className={styles.bar} aria-hidden="true">
              <i />
            </span>
          </div>
        )}
      </div>

      <p className={styles.fileName} title={file.name}>
        {file.name}
      </p>
      <p className={styles.hint}>
        Pull straight down and let go. Green dots mean it goes in. Miss, and a friend will help out.
      </p>

      <div className={styles.footer}>
        <button
          type="button"
          className={settings.ghost}
          disabled={uploading}
          onClick={close}
          data-testid="logo-hoop-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          className={`${settings.ghost} ${styles.skip}`}
          disabled={uploading}
          onClick={begin}
          data-testid="logo-hoop-skip"
        >
          Skip the game and upload
        </button>
      </div>
    </Modal>
  );
}
